import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated } from '@/core/api';

import { useIsRealtimeConnected } from '@/core/realtime';
import * as api from './api';
import type {
  AnnouncementListParams,
  AnnouncementWriteInput,
  MessageDto,
  MessageReportCreateInput,
  MessageReportDto,
  MessageReportListParams,
  MessageReportReviewInput,
  ThreadListParams,
} from './types';

/** Query-key factory — every communication key derives from here. */
export const threadKeys = {
  all: ['threads'] as const,
  lists: () => [...threadKeys.all, 'list'] as const,
  list: (params: ThreadListParams) => [...threadKeys.lists(), params] as const,
  details: () => [...threadKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...threadKeys.details(), publicId] as const,
  messages: (publicId: string) => [...threadKeys.detail(publicId), 'messages'] as const,
};

/**
 * Polling intervals. Realtime lengthens them; it never removes them
 * (FE-ADR-017). A chat that silently stops updating because a corporate proxy
 * ate the WebSocket is a worse product than one that refreshes every minute.
 */
export const THREADS_POLL_MS = 30_000;
export const THREADS_POLL_CONNECTED_MS = 2 * 60_000;
export const MESSAGES_POLL_MS = 10_000;
export const MESSAGES_POLL_CONNECTED_MS = 60_000;

export function useThreads(params: ThreadListParams = {}) {
  const connected = useIsRealtimeConnected();
  return useQuery({
    queryKey: threadKeys.list(params),
    queryFn: () => api.getThreads(params),
    refetchInterval: connected ? THREADS_POLL_CONNECTED_MS : THREADS_POLL_MS,
  });
}

/**
 * The window of a thread's history held in the cache: the newest page, plus
 * every older page the reader has asked for, in one oldest-first list.
 *
 * `olderPage` is the page number to fetch for the NEXT batch of older
 * messages, or `null` at the start of the conversation. Page boundaries are
 * stable here — messages are ordered oldest-first and are never hard-deleted,
 * so new arrivals extend the last page and never renumber the earlier ones.
 */
export interface MessageWindow extends Paginated<MessageDto> {
  olderPage: number | null;
}

/** Read the `page` a DRF pagination link points at. Page 1 omits the param. */
function pageFromLink(link: string | null): number | null {
  if (!link) return null;
  const raw = new URL(link, 'http://x').searchParams.get('page');
  if (raw === null) return 1;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function mergeNewestPage(
  cached: MessageWindow | undefined,
  newest: Paginated<MessageDto>
): MessageWindow {
  if (!cached) return { ...newest, olderPage: pageFromLink(newest.previous) };
  const arriving = new Set(newest.results.map(item => item.public_id));
  const older = cached.results.filter(item => !arriving.has(item.public_id));
  return {
    ...newest,
    results: [...older, ...newest.results],
    // The reader's own walk backwards owns this — a refetch of the newest page
    // must not forget how far back they already are.
    olderPage: cached.olderPage,
  };
}

/**
 * A thread's history, newest page first and then whatever the reader loaded
 * before it.
 *
 * The initial fetch asks for the LAST page: with oldest-first ordering, page 1
 * is the beginning of the conversation, so an unqualified fetch showed a long
 * thread's first twenty messages and never its newest ones. Older pages are
 * merged in rather than replaced, so a poll refreshing the newest page does
 * not throw away what "load older" has already brought in.
 */
export function useThreadMessages(publicId: string | null | undefined) {
  const qc = useQueryClient();
  const connected = useIsRealtimeConnected();
  return useQuery({
    queryKey: threadKeys.messages(publicId ?? ''),
    queryFn: async (): Promise<MessageWindow> => {
      const key = threadKeys.messages(publicId as string);
      const newest = await api.getMessages(publicId as string, { page: 'last' });
      return mergeNewestPage(qc.getQueryData<MessageWindow>(key), newest);
    },
    enabled: Boolean(publicId),
    refetchInterval: connected ? MESSAGES_POLL_CONNECTED_MS : MESSAGES_POLL_MS,
  });
}

/**
 * Prepend one older page into the window. The caller passes the `olderPage`
 * the window is currently reporting; the response's own `previous` link says
 * where the next batch lives, and `null` means the top of the history.
 */
export function useLoadOlderMessages(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (page: number) => api.getMessages(publicId, { page }),
    onSuccess: older => {
      qc.setQueryData<MessageWindow>(threadKeys.messages(publicId), current => {
        if (!current) return current;
        const known = new Set(current.results.map(item => item.public_id));
        return {
          ...current,
          results: [...older.results.filter(item => !known.has(item.public_id)), ...current.results],
          olderPage: pageFromLink(older.previous),
        };
      });
    },
  });
}

export function usePostMessage(publicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.postMessage(publicId, body),
    // The REST reply and the socket publication are the same message. Merging
    // both through `appendMessage` makes the dedupe rule the single place where
    // "did I already render this?" is answered (FE-ADR-017).
    onSuccess: message => {
      appendMessage(qc, publicId, message);
      void qc.invalidateQueries({ queryKey: threadKeys.lists() });
    },
  });
}

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.markThreadRead(publicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: threadKeys.lists() }),
  });
}

type QueryClientLike = ReturnType<typeof useQueryClient>;

/**
 * Insert a message into a thread's cache, deduped by `public_id`.
 *
 * This is THE rule: the poster receives their own message twice — once as the
 * POST response, once over the socket — and neither copy may double the other.
 * `public_id` is server-assigned and identical in both, so it is the identity;
 * the later copy replaces the earlier one so an edit or a soft delete that
 * arrives over the socket wins.
 *
 * A publication for a thread whose messages are not cached is dropped: there is
 * nothing on screen to update, and the next `useThreadMessages` fetch is the
 * source of truth.
 */
export function appendMessage(
  qc: QueryClientLike,
  threadPublicId: string,
  message: MessageDto
): void {
  qc.setQueryData<MessageWindow>(threadKeys.messages(threadPublicId), current => {
    if (!current) return current;
    const index = current.results.findIndex(item => item.public_id === message.public_id);
    if (index >= 0) {
      const results = [...current.results];
      results[index] = message;
      return { ...current, results };
    }
    return { ...current, count: current.count + 1, results: [...current.results, message] };
  });
}

/** Query-key factory for the announcement feed. */
export const announcementKeys = {
  all: ['announcements'] as const,
  lists: () => [...announcementKeys.all, 'list'] as const,
  list: (params: AnnouncementListParams) => [...announcementKeys.lists(), params] as const,
};

/** Query-key factory for the moderation queue. */
export const reportKeys = {
  all: ['message-reports'] as const,
  lists: () => [...reportKeys.all, 'list'] as const,
  list: (params: MessageReportListParams) => [...reportKeys.lists(), params] as const,
};

export function useThread(publicId: string | null | undefined) {
  return useQuery({
    queryKey: threadKeys.detail(publicId ?? ''),
    queryFn: () => api.getThread(publicId as string),
    enabled: Boolean(publicId),
  });
}

/**
 * Get-or-create a direct thread. Deliberately no client-side matrix check:
 * the server owns the rules (backend ADR-020) and its 403 message is what the
 * picker renders.
 */
export function useCreateDirectThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberPublicId: string) => api.createDirectThread(memberPublicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: threadKeys.lists() }),
  });
}

export function useSetThreadMuted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, isMuted }: { publicId: string; isMuted: boolean }) =>
      api.setThreadMuted(publicId, isMuted),
    onSuccess: thread => {
      qc.setQueryData(threadKeys.detail(thread.public_id), thread);
      void qc.invalidateQueries({ queryKey: threadKeys.lists() });
    },
  });
}

/**
 * Soft-delete a message. The row keeps its place with an empty body, so the
 * thread is refetched rather than spliced — the server decides what "removed"
 * renders as.
 */
export function useDeleteMessage(threadPublicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messagePublicId: string) => api.deleteMessage(messagePublicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadKeys.messages(threadPublicId) });
      void qc.invalidateQueries({ queryKey: threadKeys.lists() });
    },
  });
}

export function useReportMessage() {
  return useMutation({
    mutationFn: (input: MessageReportCreateInput) => api.createMessageReport(input),
  });
}

// --- announcements ---------------------------------------------------------

export function useAnnouncements(params: AnnouncementListParams = {}) {
  return useQuery({
    queryKey: announcementKeys.list(params),
    queryFn: () => api.getAnnouncements(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AnnouncementWriteInput) => api.createAnnouncement(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementKeys.all }),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      input,
    }: {
      publicId: string;
      input: Partial<AnnouncementWriteInput>;
    }) => api.updateAnnouncement(publicId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementKeys.all }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteAnnouncement(publicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementKeys.all }),
  });
}

export function useSetAnnouncementPinned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, pinned }: { publicId: string; pinned: boolean }) =>
      api.setAnnouncementPinned(publicId, pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: announcementKeys.all }),
  });
}

// --- moderation ------------------------------------------------------------

export function useMessageReports(params: MessageReportListParams = {}) {
  return useQuery({
    queryKey: reportKeys.list(params),
    queryFn: () => api.getMessageReports(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Review one report. The row is patched optimistically (the queue is usually
 * filtered to `open`, so the reviewed row should leave immediately) and the
 * list is refetched afterwards for the server's own read-back.
 */
export function useReviewMessageReport(params: MessageReportListParams) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: MessageReportReviewInput }) =>
      api.reviewMessageReport(publicId, input),
    onMutate: ({ publicId, input }) => {
      const key = reportKeys.list(params);
      const previous = qc.getQueryData<Paginated<MessageReportDto>>(key);
      qc.setQueryData<Paginated<MessageReportDto>>(key, current => {
        if (!current) return current;
        const nextStatus: MessageReportDto['status'] =
          input.action === 'dismiss' ? 'dismissed' : 'reviewed';
        const results = current.results.map(row =>
          row.public_id === publicId ? { ...row, status: nextStatus } : row
        );
        return { ...current, results };
      });
      return { previous, key };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) qc.setQueryData(context.key, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: reportKeys.all });
      void qc.invalidateQueries({ queryKey: threadKeys.all });
    },
  });
}
